import { useState } from 'react';

import DownloadIcon from '@mui/icons-material/Download';
import {
  IconButton,
  CircularProgress,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import { saveAs } from 'file-saver';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { ButtonActionListT } from 'shared/components/Button/Action/ButtonActionList/ButtonActionList';
import SeeDetailsPage from 'shared/containers/SeeDetailsPage';
import {
  GENERAL_INFO_TITLE, SETTINGS_TITLE, STATUS_INFO_TITLE, ViewDetailsT,
} from 'shared/containers/ViewDetails/ViewDetails';
import { IPNetworkT, MulticastInfoT } from 'shared/models/ip-networks';
import api from 'utils/api';
import { formatDateFromNow } from 'utils/date';

import './IPNetworksDetails.scss';

export interface DialogProps {
  id: string;
  isOpen: boolean;
  onClose: () => void;
}
function DialogDownloadVPNAgent({ id, onClose, isOpen }: DialogProps) {
  const [isDownloading, setIsDownloading] = useState('');

  const download = (targetId: string, architecture: string) => {
    setIsDownloading(architecture);
    api.IPNetwork.downloadVPNAgent(targetId, architecture)
      .then((resp: Response) => {
        if (resp.status >= 400) {
          toast.error(`Failed to download the VPN Agent ${architecture} - Error ${resp.status} ${resp.statusText}`);
          setIsDownloading('');
        } else {
          resp.blob()
            .then((data: Blob) => {
              try {
                saveAs(data, `vpn-agent-${id}-${architecture}.zip`);
              } catch {
                toast.error('Failed to load in browser the received VPN Agent file');
              }
              setIsDownloading('');
            });
        }
      });
  };

  interface Architecture {
    title: string;
    ref: string;
  }
  const architectures: Architecture[] = [
    {
      title: 'VPN agent for x86 architecture',
      ref: 'x86',
    },
    {
      title: 'VPN agent for ARM architecture',
      ref: 'arm',
    },
    {
      title: 'VPN agent for ARM64 architecture',
      ref: 'arm64',
    },
  ];
  /* eslint-disable max-len */
  return (
    <Dialog onClose={onClose} open={isOpen}>
      <div className="dialog-component">
        <h1>Download VPN agent</h1>
        <p>Virtual private network agent is a gateway to send the network packets the IPCore to be later sent to the LPWAN devices. VPN agent can be run on x86 Linux machine and as well as arm based machines (raspberry pi) with sudo permission. The root permission is needed since the VPN agent creates a tunnel interface to receive and forward the network packets.</p>
        <p>It can either support IPv6 traffic or IPv4 traffic depending on its IP network.Note that the dual-stack feature is not supported yet. The IP address of the tunnel created by the VPN agent will have the first IP address from its configured IP Network in the IPCore. The traffic to the LPWAN devices are forwarded through this tunnel to the IPCore.</p>
        <h3>Architecture</h3>
        <ul className="dialog-component__architectures">
          {
            architectures.map((arch) => (
              <li key={arch.ref}>
                {arch.title}
                <IconButton
                  onClick={() => download(id, arch.ref)}
                >
                  {
                    isDownloading === arch.ref
                      ? <CircularProgress color="secondary" size={25} />
                      : <DownloadIcon />
                  }
                </IconButton>
              </li>
            ))
          }
        </ul>
      </div>
    </Dialog>
  );
}

function IPNetworksDetails() {
  const [openDownloadVPNAgent, setOpenDownloadVPNAgent] = useState(false);
  const params: any = useParams();

  const dataStructure: ViewDetailsT = {
    sections: [
      {
        title: STATUS_INFO_TITLE,
        renderSubtitleFn: (data: IPNetworkT) => (data.statusReportedAt ? `Updated ${formatDateFromNow(data.statusReportedAt)}` : ''),
        items: [
          {
            type: 'status',
            label: 'VPN Downlink (Tx)',
            dataPath: 'nbaDownlinkStatus',
          },
          {
            type: 'status',
            label: 'VPN Uplink (Rx)',
            dataPath: 'nbaUplinkStatus',
          },
        ],
        shape: 2,
      },
      {
        title: GENERAL_INFO_TITLE,
        items: [
          {
            type: 'name',
          },
          {
            type: 'creation',
          },
          {
            type: 'update',
          },
        ],
        shape: 2,
      },
      {
        title: SETTINGS_TITLE,
        items: [
          {
            label: 'Address Type',
            dataPath: 'addressType',
            renderFn: (value: string) => (value === 'V4' ? 'IPv4' : 'IPv6'),
          },
          {
            label: 'Prefix',
            dataPath: 'prefix',
          },
          {
            label: 'First Address',
            dataPath: 'first',
          },
          {
            label: 'Last Address',
            dataPath: 'last',
          },
          {
            label: 'Reserved',
            dataPath: 'reserved',
          },
          {
            label: 'Free',
            dataPath: 'free',
          },
          {
            label: 'Multicast Information',
            dataPath: 'multicastInfos',
            renderFn: (obj: MulticastInfoT) => `IP: ${obj.ip} · Port: ${obj.port}`,
          },
        ],
        shape: 2,
      },
    ],
  };

  const actions: Array<ButtonActionListT> = [
    {
      name: 'Download VPN Agent',
      fn: () => {
        setOpenDownloadVPNAgent(true);
      },
      icon: <DownloadIcon />,
    },
  ];
  return (
    <>
      <DialogDownloadVPNAgent
        id={params.id}
        isOpen={openDownloadVPNAgent}
        onClose={() => setOpenDownloadVPNAgent(false)}
      />
      <SeeDetailsPage
        name="IP Network"
        targetID={params.id}
        fromAPI={api.IPNetwork}
        defaultURI="/ip-networks"
        dataStructure={dataStructure}
        actions={actions}
      />
    </>
  );
}

export default IPNetworksDetails;
